import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import VideoPlayer from '../components/VideoPlayer';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen pt-[72px] overflow-hidden flex items-center justify-center bg-[#0a0a0f]/30 backdrop-blur-sm"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[800px] h-[800px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(var(--primary-rgb),0.4) 0%, transparent 70%)',
            top: '-20%',
            left: '-10%',
            animation: 'float 20s ease-in-out infinite',
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(var(--accent-cyan-rgb),0.4) 0%, transparent 70%)',
            top: '30%',
            right: '-15%',
            animation: 'float 15s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 flex flex-col items-center">
        {/* New Badge */}
        <div
          className={`mb-10 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] backdrop-blur-md transition-all duration-700 hover:bg-[rgba(255,255,255,0.08)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
          </span>
          <span className="text-sm font-medium text-indigo-300 tracking-wide">{t.hero.newBadge}</span>
          <span className="w-[1px] h-3 bg-white/10 mx-1"></span>
          <span className="text-sm text-[var(--ds-text-secondary)]">{t.hero.newBadgeText}</span>
        </div>

        {/* Title */}
        <div className="text-center mb-10 max-w-6xl">
          <h1
            className={`flex flex-col gap-2 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.1] transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <span className="block font-serif italic text-gradient-hero py-2">{t.hero.titleLine1}</span>
            <span className="block font-serif italic text-gradient-brand py-2">{t.hero.titleLine2}</span>
            {t.hero.titleLine3 && <span>{t.hero.titleLine3}</span>}
          </h1>
          {t.hero.subtitle && (
            <p className={`mt-8 text-2xl sm:text-3xl font-light text-[var(--ds-text-secondary)] transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {t.hero.subtitle}
            </p>
          )}
        </div>

        {/* Description */}
        <p
          className={`text-lg sm:text-xl text-white text-center max-w-3xl mb-16 leading-relaxed transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '300ms' }}
        >
          {t.hero.description}
        </p>

        {/* Video Demo Container */}
        <div
          className={`relative w-full max-w-5xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          style={{ transitionDelay: '500ms' }}
        >
          {/* Video Player - Clean Subtle Style */}
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.06] shadow-2xl shadow-black/50 bg-[#0a0a0f]">
            <VideoPlayer
              src="/video.mp4"
              alt={t.hero.videoAlt || 'DesignSolution AI户型设计平台演示'}
              autoPlay={false}
              loop={true}
              muted={false}
              className=""
            />
          </div>
        </div>
      </div>
    </section>
  );
}
